import * as React from 'react'

import { getBridgeSupabaseAccessToken } from './bridge-auth'
import { useSession } from './session'
import { getSupabase } from './supabase'

/**
 * Tempo real (pedido do dono, 29/09): ver o andamento de chamado, atividade e
 * nota sem F5. Uma conexão só para o app inteiro, no Realtime do Supabase:
 * o banco publica as mudanças destas tabelas (migration 20261015100000) e o
 * Realtime entrega a cada pessoa SÓ o que o RLS dela deixa ler — o mesmo
 * filtro das telas. Cada tela assina as tabelas que mostra e relê o que mudou.
 *
 * Sem conexão (rede, Realtime fora), nada quebra: as telas seguem com o que
 * já faziam (ler ao abrir, o sino a cada 30 s).
 */

export const LIVE_TABLES = [
  'project',
  'project_stage',
  'board_column',
  'activity',
  'activity_event',
  'activity_label',
  'project_message',
  'ticket',
  'ticket_activity',
  'ticket_message',
  'notification',
  'note_touch',
  'note_share',
] as const

export type LiveTable = (typeof LIVE_TABLES)[number]

export interface LiveChange {
  readonly table: LiveTable
  readonly eventType: 'INSERT' | 'UPDATE' | 'DELETE'
  /** A linha nova (vazia no DELETE). */
  readonly row: Readonly<Record<string, unknown>>
  /** A linha antiga: no DELETE, ao menos a chave. */
  readonly old: Readonly<Record<string, unknown>>
}

type Listener = (change: LiveChange) => void
const listeners = new Map<LiveTable, Set<Listener>>()

export function onLiveChange(table: LiveTable, listener: Listener): () => void {
  let set = listeners.get(table)
  if (!set) {
    set = new Set()
    listeners.set(table, set)
  }
  set.add(listener)
  return () => {
    set?.delete(listener)
  }
}

/** Exposto para teste: é por aqui que a conexão entrega cada mudança. */
export function emitLiveChange(change: LiveChange): void {
  listeners.get(change.table)?.forEach((listener) => {
    try {
      listener(change)
    } catch (error) {
      console.warn('[tempo real] ouvinte falhou', error)
    }
  })
}

export type LiveStatus = 'desligado' | 'conectando' | 'ao_vivo' | 'caiu'
let status: LiveStatus = 'desligado'
const statusListeners = new Set<() => void>()

function setStatus(next: LiveStatus) {
  if (status === next) return
  status = next
  statusListeners.forEach((notify) => notify())
}

export function useLiveStatus(): LiveStatus {
  return React.useSyncExternalStore(
    (notify) => {
      statusListeners.add(notify)
      return () => statusListeners.delete(notify)
    },
    () => status,
  )
}

/**
 * A conexão, uma para o app. Fica fora do React: montar de novo (StrictMode em
 * dev, troca de tela que remonta o provider) reaproveita a que já existe — tirar
 * o último canal derruba o socket, e o canal novo ficaria pendurado.
 */
let connection: { close: () => void } | null = null
let teardown: number | null = null

function openConnection(): { close: () => void } {
  const sb = getSupabase()
  // O token do bridge vai no cabeçalho HTTP (fetch); o socket precisa dele à
  // parte, senão entra como `anon` e o RLS não entrega nada. No login de
  // desenvolvimento a própria sessão do supabase-js cuida disso.
  const applyAuth = () => {
    const token = getBridgeSupabaseAccessToken()
    if (token) void sb.realtime.setAuth(token)
  }
  applyAuth()

  // Cai (rede, Realtime reiniciando, primeira montagem da replicação que
  // passa do prazo): tenta de novo com espera crescente, até 1 min.
  let channel: ReturnType<typeof sb.channel> | null = null
  let retry: number | null = null
  let attempt = 0
  let closed = false
  const connect = () => {
    if (closed) return
    let next = sb.channel(`hub-banco-${Date.now()}`)
    for (const table of LIVE_TABLES) {
      next = next.on('postgres_changes', { event: '*', schema: 'sistemas', table }, (payload) =>
        emitLiveChange({
          table,
          eventType: payload.eventType as LiveChange['eventType'],
          row: (payload.new ?? {}) as Record<string, unknown>,
          old: (payload.old ?? {}) as Record<string, unknown>,
        }),
      )
    }
    channel = next
    setStatus('conectando')
    next.subscribe((state) => {
      if (closed || channel !== next) return
      if (state === 'SUBSCRIBED') {
        attempt = 0
        setStatus('ao_vivo')
      } else if (state === 'CHANNEL_ERROR' || state === 'TIMED_OUT' || state === 'CLOSED') {
        // Solta ANTES de remover: remover dispara outro CLOSED, que cai no
        // `channel !== next` acima em vez de recursar.
        channel = null
        setStatus('caiu')
        void sb.removeChannel(next)
        const wait = Math.min(60_000, 2_000 * 2 ** attempt)
        attempt += 1
        retry = window.setTimeout(connect, wait)
      }
    }, 30_000)
  }
  connect()
  // O token do bridge renova de tempos em tempos: o socket recebe o novo
  // antes de o velho vencer.
  const timer = window.setInterval(applyAuth, 60_000)

  return {
    close: () => {
      closed = true
      window.clearInterval(timer)
      if (retry !== null) window.clearTimeout(retry)
      const current = channel
      channel = null
      if (current) void sb.removeChannel(current)
      setStatus('desligado')
    },
  }
}

/** Abre a conexão quando há sessão e fecha no logout. */
export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const { status: session } = useSession()

  React.useEffect(() => {
    if (session !== 'autenticado') return
    if (teardown !== null) {
      window.clearTimeout(teardown)
      teardown = null
    }
    connection ??= openConnection()
    return () => {
      teardown = window.setTimeout(() => {
        connection?.close()
        connection = null
        teardown = null
      }, 1_000)
    }
  }, [session])

  return <>{children}</>
}

/**
 * Relê quando alguma das tabelas muda. Mudanças em rajada (mover um card
 * grava a atividade e o evento) viram UMA leitura depois de `delayMs`.
 * `accept` filtra (ex.: só o chamado aberto). `refresh` pode pedir para
 * esperar (devolvendo `false`) — o store faz isso enquanto há escrita
 * própria em voo, para a tela não "voltar" um card no meio do arrasto.
 */
export function useLiveRefresh(
  tables: readonly LiveTable[],
  refresh: () => void | boolean | Promise<void | boolean>,
  options: { delayMs?: number; accept?: (change: LiveChange) => boolean; enabled?: boolean } = {},
): void {
  const { delayMs = 600, enabled = true } = options
  const refreshRef = React.useRef(refresh)
  refreshRef.current = refresh
  const acceptRef = React.useRef(options.accept)
  acceptRef.current = options.accept
  const key = tables.join(',')

  React.useEffect(() => {
    if (!enabled) return
    let timer: number | null = null
    const fire = async () => {
      timer = null
      const done = await refreshRef.current()
      if (done === false) timer = window.setTimeout(() => void fire(), delayMs)
    }
    const schedule = (change: LiveChange) => {
      if (acceptRef.current && !acceptRef.current(change)) return
      if (timer !== null) window.clearTimeout(timer)
      timer = window.setTimeout(() => void fire(), delayMs)
    }
    const offs = (key.split(',') as LiveTable[]).map((table) => onLiveChange(table, schedule))
    return () => {
      offs.forEach((off) => off())
      if (timer !== null) window.clearTimeout(timer)
    }
  }, [key, delayMs, enabled])
}
